// Hooks de datos de la pantalla Cobranza contra `loan-collector-assignment-business`.
// Aquí no se arma ninguna URL ni se toca `fetch`: eso vive en `api/services/loan-collector-assignment/`.
import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateAssignmentRuleRequest } from '@fiado/type-kit/bin/loanCollectorAssignment/dtos/requests/CreateAssignmentRuleRequest';
import type { UpdateAssignmentRuleRequest } from '@fiado/type-kit/bin/loanCollectorAssignment/dtos/requests/UpdateAssignmentRuleRequest';
import type { AssignmentRuleResponse } from '@fiado/type-kit/bin/loanCollectorAssignment/dtos/responses/AssignmentRuleResponse';
import type { CollectorAssignmentResponse } from '@fiado/type-kit/bin/loanCollectorAssignment/dtos/responses/CollectorAssignmentResponse';
import type { RedistributeSummaryResponse } from '@fiado/type-kit/bin/loanCollectorAssignment/dtos/responses/RedistributeSummaryResponse';
import type { RetailUser } from '@fiado/type-kit/bin/retailOrg/dtos/RetailUser';
import type { Store } from '@fiado/type-kit/bin/retailOrg/dtos/Store';
import { collectorAssignmentApi } from '@/api/services/loan-collector-assignment';
import type { CollectorAssignmentQuery } from '@/api/services/loan-collector-assignment';
import { listRetailUsers, listStores } from '@/api/services/retail-org/retail-org.client';
import type { AssignmentRow, CollectorLoadRow, RuleRow } from '../types';
import { toAssignmentRow, toCollectorLoadRow } from './mappers';
import type { RetailOrgSnapshot, RetailUsersById, StoresById } from './mappers';

/** Raíz de las query keys de la pantalla; la UI la usa para reintentar toda la lectura. */
export const COLLECTIONS_KEY = 'collections';

const EMPTY_USERS: RetailUsersById = new Map<string, RetailUser>();
const EMPTY_STORES: StoresById = new Map<string, Store>();

/** Toda query de la pantalla arranca cuando hay retailer: sin él no hay nada que consultar. */
function isReady(retailerId: string | undefined): retailerId is string {
    return retailerId !== undefined && retailerId !== '';
}

/** El directorio ya indexado, más el estado de su lectura. */
export interface RetailOrgDirectory extends RetailOrgSnapshot {
    isLoading: boolean;
}

interface DirectorySnapshot {
    users: RetailUser[];
    stores: Store[];
    unavailable: boolean;
}

/**
 * Usuarios retail y tiendas de la cadena, indexados por id. Va con `allSettled` porque un directorio
 * caído NO tumba la pantalla de cobranza: la query nunca rechaza y por eso no expone `error`.
 */
export function useRetailOrgDirectory(retailerId: string | undefined): RetailOrgDirectory {
    const enabled = isReady(retailerId);
    const directory = useQuery({
        queryKey: [COLLECTIONS_KEY, 'directory', retailerId],
        enabled,
        staleTime: 300_000,
        queryFn: async (): Promise<DirectorySnapshot> => {
            const [users, stores] = await Promise.allSettled([
                listRetailUsers(retailerId ?? ''),
                listStores(retailerId ?? ''),
            ]);
            return {
                users: users.status === 'fulfilled' ? users.value : [],
                stores: stores.status === 'fulfilled' ? stores.value : [],
                unavailable: users.status === 'rejected' || stores.status === 'rejected',
            };
        },
    });

    return useMemo(() => {
        const snapshot = directory.data;
        if (snapshot === undefined) {
            return {
                retailUsersById: EMPTY_USERS,
                storesById: EMPTY_STORES,
                unavailable: false,
                isLoading: enabled && directory.isLoading,
            };
        }
        return {
            retailUsersById: new Map(snapshot.users.map((user) => [user.retailUserId, user])),
            storesById: new Map(snapshot.stores.map((store) => [store.storeId, store])),
            unavailable: snapshot.unavailable,
            isLoading: false,
        };
    }, [directory.data, directory.isLoading, enabled]);
}

export interface CollectorLoadsResult {
    rows: CollectorLoadRow[];
    directoryUnavailable: boolean;
    isLoading: boolean;
    /** El error del lambda, tal cual: la UI lo traduce con `userMessageOf`. */
    error: Error | null;
}

/** La carga activa de cada cobrador del retailer, ya cruzada con el directorio de la cadena. */
export function useCollectorLoads(retailerId: string | undefined): CollectorLoadsResult {
    const enabled = isReady(retailerId);
    const { retailUsersById, storesById, unavailable, isLoading } =
        useRetailOrgDirectory(retailerId);
    const loads = useQuery({
        queryKey: [COLLECTIONS_KEY, 'loads', retailerId],
        enabled,
        staleTime: 30_000,
        queryFn: () => collectorAssignmentApi.listCollectorLoads(retailerId ?? ''),
    });

    const rows = useMemo(
        () =>
            (loads.data?.loads ?? []).map((dto) =>
                toCollectorLoadRow(dto, { retailUsersById, storesById, unavailable }),
            ),
        [loads.data, retailUsersById, storesById, unavailable],
    );

    return {
        rows,
        directoryUnavailable: unavailable,
        // Con la query deshabilitada `isLoading` de react-query sigue en `true`: eso pintaría un
        // cargando eterno en la pantalla sin retailer.
        isLoading: enabled && (loads.isLoading || isLoading),
        error: enabled ? loads.error : null,
    };
}

export interface CollectorAssignmentsResult {
    rows: AssignmentRow[];
    directoryUnavailable: boolean;
    isLoading: boolean;
    error: Error | null;
}

/**
 * Las asignaciones del retailer. Los filtros que van en `serverFilters` los resuelve el lambda y
 * forman parte de la clave de caché; el resto se filtra en el cliente con `filterAssignments`.
 */
export function useCollectorAssignments(
    retailerId: string | undefined,
    serverFilters: CollectorAssignmentQuery = {},
): CollectorAssignmentsResult {
    const enabled = isReady(retailerId);
    const { retailUsersById, storesById, unavailable, isLoading } =
        useRetailOrgDirectory(retailerId);
    const assignments = useQuery({
        queryKey: [COLLECTIONS_KEY, 'assignments', retailerId, serverFilters],
        enabled,
        staleTime: 30_000,
        queryFn: () =>
            collectorAssignmentApi.listCollectorAssignments(retailerId ?? '', serverFilters),
    });

    const rows = useMemo(
        () =>
            (assignments.data?.assignments ?? []).map((dto) =>
                toAssignmentRow(dto, { retailUsersById, storesById, unavailable }),
            ),
        [assignments.data, retailUsersById, storesById, unavailable],
    );

    return {
        rows,
        directoryUnavailable: unavailable,
        isLoading: enabled && (assignments.isLoading || isLoading),
        error: enabled ? assignments.error : null,
    };
}

export interface AssignmentRulesResult {
    rules: RuleRow[];
    isLoading: boolean;
    error: Error | null;
}

/** Las reglas del retailer, activa e históricas. Cuál manda hoy lo decide `activeRule` de `logic`. */
export function useAssignmentRules(retailerId: string | undefined): AssignmentRulesResult {
    const enabled = isReady(retailerId);
    const rules = useQuery({
        queryKey: [COLLECTIONS_KEY, 'rules', retailerId],
        enabled,
        staleTime: 60_000,
        queryFn: () => collectorAssignmentApi.listAssignmentRules(retailerId ?? ''),
    });

    return {
        rules: rules.data?.rules ?? [],
        isLoading: enabled && rules.isLoading,
        error: enabled ? rules.error : null,
    };
}

/** Mover cartera cambia la carga de dos cobradores y el historial de la asignación. */
function useInvalidateAssignments(retailerId: string | undefined) {
    const queryClient = useQueryClient();
    return () => {
        void queryClient.invalidateQueries({ queryKey: [COLLECTIONS_KEY, 'loads', retailerId] });
        void queryClient.invalidateQueries({
            queryKey: [COLLECTIONS_KEY, 'assignments', retailerId],
        });
    };
}

/** Tocar la regla activa cambia el `threshold` con el que se lee la carga de cada cobrador. */
function useInvalidateRules(retailerId: string | undefined) {
    const queryClient = useQueryClient();
    return () => {
        void queryClient.invalidateQueries({ queryKey: [COLLECTIONS_KEY, 'rules', retailerId] });
        void queryClient.invalidateQueries({ queryKey: [COLLECTIONS_KEY, 'loads', retailerId] });
    };
}

export interface ReassignInput {
    creditId: string;
    reason: string;
}

/** Reasigna UN crédito a otro cobrador. `reason` es obligatorio: queda en la auditoría del lambda. */
export function useReassignAssignment(retailerId: string | undefined) {
    const invalidate = useInvalidateAssignments(retailerId);
    return useMutation<CollectorAssignmentResponse, Error, ReassignInput>({
        mutationFn: (input) =>
            collectorAssignmentApi.reassignAssignment(
                retailerId ?? '',
                input.creditId,
                input.reason,
            ),
        onSettled: invalidate,
    });
}

export interface RedistributeInput {
    collectorUserId: string;
    reason: string;
}

/** Reparte la cartera activa de un cobrador. Un 200 puede traer `errors`: no todos se movieron. */
export function useRedistributeCollector(retailerId: string | undefined) {
    const invalidate = useInvalidateAssignments(retailerId);
    return useMutation<RedistributeSummaryResponse, Error, RedistributeInput>({
        mutationFn: (input) =>
            collectorAssignmentApi.redistributeCollector(
                retailerId ?? '',
                input.collectorUserId,
                input.reason,
            ),
        onSettled: invalidate,
    });
}

export interface ActivateRuleInput {
    ruleId: string;
}

/** Activa una regla existente; el lambda desactiva la anterior. */
export function useActivateRule(retailerId: string | undefined) {
    const invalidate = useInvalidateRules(retailerId);
    return useMutation<AssignmentRuleResponse, Error, ActivateRuleInput>({
        mutationFn: (input) =>
            collectorAssignmentApi.activateAssignmentRule(retailerId ?? '', input.ruleId),
        onSettled: invalidate,
    });
}

/** Crea una regla. Los pesos deben sumar 100 — `areWeightsValid` lo chequea antes de mandar. */
export function useCreateRule(retailerId: string | undefined) {
    const invalidate = useInvalidateRules(retailerId);
    return useMutation<AssignmentRuleResponse, Error, CreateAssignmentRuleRequest>({
        mutationFn: (body) => collectorAssignmentApi.createAssignmentRule(retailerId ?? '', body),
        onSettled: invalidate,
    });
}

export interface UpdateRuleInput {
    ruleId: string;
    body: UpdateAssignmentRuleRequest;
}

/** Edita una regla existente (update parcial). */
export function useUpdateRule(retailerId: string | undefined) {
    const invalidate = useInvalidateRules(retailerId);
    return useMutation<AssignmentRuleResponse, Error, UpdateRuleInput>({
        mutationFn: (input) =>
            collectorAssignmentApi.updateAssignmentRule(retailerId ?? '', input.ruleId, input.body),
        onSettled: invalidate,
    });
}
